import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalDashboard } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Package, Truck, AlertTriangle, FileText } from "lucide-react";
import { ShipmentIssuesPanel } from "@/components/portal/ShipmentIssuesPanel";

export const Route = createFileRoute("/_authenticated/portal/workspace")({ component: CustomerWorkspace });

function CustomerWorkspace() {
  const fetchDashboard = useServerFn(getPortalDashboard);
  const { data: payload } = useQuery({
    queryKey: ["portal-workspace"],
    queryFn: () => runServerFn(() => fetchDashboard(), "Loading workspace"),
  });
  const data = payload?.kpis;
  const recentOrders = payload?.recentOrders;
  const recentShipments = payload?.recentShipments;

  const cards = [
    { label: "Open orders", value: data?.openOrders ?? 0, icon: Package, to: "/portal/orders" as const },
    { label: "On-hand units", value: data?.onHand ?? 0, icon: Package, to: "/portal/inventory" as const },
    { label: "Inbound shipments", value: data?.inbound ?? 0, icon: Truck, to: "/portal/inbound" as const },
    { label: "Low-stock SKUs", value: data?.lowStockCount ?? 0, icon: AlertTriangle, to: "/portal/inventory" as const },
    { label: "Open invoices", value: data?.openInvoices ?? 0, icon: FileText, to: "/portal/invoices" as const },
  ];
  return (
    <>
      <PortalPageHeader title="Customer Workspace" description="Your brand's view of the warehouse." />
      <div className="p-4 sm:p-6 space-y-6">
        <ShipmentIssuesPanel />
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
          {cards.map((c) => (
            <Link key={c.label} to={c.to} className="block">
              <Card className="transition hover:border-primary/50 hover:shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
                  <c.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent><div className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">{c.value}</div></CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Recent Orders</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Created</TableHead></TableRow></TableHeader>
                <TableBody>
                  {recentOrders && recentOrders.length > 0 ? recentOrders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell className="font-medium">{o.order_number}</TableCell>
                      <TableCell><StatusPill tone={statusTone(o.status)}>{o.status}</StatusPill></TableCell>
                      <TableCell className="text-right text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  )) : (<TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No orders yet.</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Recent Shipments</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Tracking</TableHead><TableHead>Carrier</TableHead><TableHead className="text-right">Shipped</TableHead></TableRow></TableHeader>
                <TableBody>
                  {recentShipments && recentShipments.length > 0 ? recentShipments.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.tracking_number ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground uppercase text-xs">{s.carrier ?? "—"}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{s.shipped_at ? new Date(s.shipped_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  )) : (<TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No shipments yet.</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}